import React from "react";
import { col, T, Tm, fmt, peso } from "./kit";

const W = 600;
const MONTHS = "JFMAMJJASOND";

// Baseline + trend (dashed) and the seasonal forecast it produces over 24 months.
export function Decomp({ baseline, trend, season }) {
  const H = 240, L = 36, R = 14, top = 18, bot = 30, n = 24;
  const base = Array.from({ length: n }, (_, m) => baseline + trend * m);
  const fc = base.map((b, m) => b * season[m % 12]);
  const max = Math.max(...fc) * 1.1, min = Math.min(...fc) * 0.85;
  const X = (m) => L + (W - L - R) * (m / (n - 1)), Y = (v) => top + (H - top - bot) * (1 - (v - min) / (max - min));
  const line = (arr) => arr.map((v, m) => `${X(m)},${Y(v)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[420px]" role="img" aria-label="Forecast decomposition">
      {[3, 15].map(m => <rect key={m} x={X(m) - 10} width={20} y={top} height={H - top - bot} fill={col("warning", 0.08)} />)}
      <polyline points={line(base)} fill="none" stroke={col("muted")} strokeWidth={1.5} strokeDasharray="6 4" />
      <polyline points={line(fc)} fill="none" stroke={col("primary")} strokeWidth={2.2} />
      {fc.map((v, m) => <circle key={m} cx={X(m)} cy={Y(v)} r={2.4} fill={col("primary")} />)}
      {fc.map((_, m) => <text key={m} x={X(m)} y={H - 12} textAnchor="middle" style={{ ...Tm, fontSize: 9.5 }}>{MONTHS[m % 12]}</text>)}
      <text x={X(3)} y={Y(fc[3]) - 8} textAnchor="middle" style={{ ...Tm, fill: col("warning") }}>Apr ×{season[3]}</text>
      <text x={X(23) - 4} y={Y(base[23]) - 8} textAnchor="end" style={{ ...Tm, fill: col("muted") }}>baseline + trend</text>
      <text x={L} y={12} style={{ ...Tm, fill: col("primary") }}>forecast = (baseline + trend × month) × seasonal index</text>
      <text x={L} y={H - 1} style={{ ...T, fontSize: 9.5 }}>Year 1</text>
      <text x={X(12)} y={H - 1} style={{ ...T, fontSize: 9.5 }}>Year 2</text>
    </svg>
  );
}

// Forecast vs actual pairs with the absolute error above each pair.
export function Accuracy({ rows }) {
  const H = 220, L = 30, top = 24, bot = 30, gw = (W - L) / rows.length;
  const max = Math.max(...rows.flatMap(r => [r.forecast, r.actual])) * 1.15;
  const Y = (v) => top + (H - top - bot) * (1 - v / max);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[420px]" role="img" aria-label="Forecast against actual">
      {rows.map((r, i) => {
        const x = L + gw * i + gw * 0.18, bw = gw * 0.3;
        return (
          <g key={r.label}>
            <rect x={x} width={bw} y={Y(r.forecast)} height={Y(0) - Y(r.forecast)} fill={col("primary", 0.85)} rx={1} />
            <rect x={x + bw + 4} width={bw} y={Y(r.actual)} height={Y(0) - Y(r.actual)} fill={col("accent", 0.6)} rx={1} />
            <text x={x + bw + 2} y={Y(Math.max(r.forecast, r.actual)) - 6} textAnchor="middle" style={{ ...Tm, fill: col("warning") }}>|{Math.abs(r.forecast - r.actual)}|</text>
            <text x={x + bw + 2} y={H - 10} textAnchor="middle" style={Tm}>{r.label}</text>
          </g>
        );
      })}
      <rect x={L} y={4} width={10} height={10} fill={col("primary", 0.85)} /><text x={L + 14} y={13} style={T}>Forecast</text>
      <rect x={L + 80} y={4} width={10} height={10} fill={col("accent", 0.6)} /><text x={L + 94} y={13} style={T}>Actual</text>
      <line x1={L} x2={W} y1={Y(0)} y2={Y(0)} stroke={col("border-strong")} />
    </svg>
  );
}

function simulate({ daily, lead, rop, order, days, start }) {
  const pts = [], orders = [];
  let stock = start, due = null;
  for (let d = 0; d <= days; d++) {
    if (due === d) { stock += order; orders[orders.length - 1].arrive = d; due = null; }
    pts.push([d, stock]);
    if (due === null && stock <= rop) { due = d + lead; orders.push({ at: d }); }
    stock = Math.max(0, stock - daily);
  }
  return { pts, orders };
}

// Inventory sawtooth: order at the reorder point, arrival one lead time later, safety stock as the floor.
export function Sawtooth(p) {
  const H = 250, L = 40, R = 14, top = 14, bot = 30;
  const { pts, orders } = simulate(p);
  const max = Math.max(...pts.map(x => x[1])) * 1.08;
  const X = (d) => L + (W - L - R) * (d / p.days), Y = (v) => top + (H - top - bot) * (1 - v / max);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[420px]" role="img" aria-label="Inventory sawtooth with reorder point">
      <rect x={L} width={W - L - R} y={Y(p.ss)} height={Y(0) - Y(p.ss)} fill={col("success", 0.1)} />
      <line x1={L} x2={W - R} y1={Y(p.rop)} y2={Y(p.rop)} stroke={col("warning")} strokeDasharray="5 4" />
      <text x={W - R} y={Y(p.rop) - 5} textAnchor="end" style={{ ...Tm, fill: col("warning") }}>reorder point {p.rop}</text>
      <text x={W - R} y={Y(p.ss) - 5} textAnchor="end" style={{ ...Tm, fill: col("success") }}>safety stock {p.ss}</text>
      <polyline points={pts.map(([d, v]) => `${X(d)},${Y(v)}`).join(" ")} fill="none" stroke={col("primary")} strokeWidth={2} />
      {orders.map((o, i) => (
        <g key={i}>
          <circle cx={X(o.at)} cy={Y(pts[o.at][1])} r={4} fill={col("warning")} />
          {o.arrive !== undefined && <>
            <line x1={X(o.at)} x2={X(o.arrive)} y1={Y(0) - 8 - i * 10} y2={Y(0) - 8 - i * 10} stroke={col("muted")} markerEnd="url(#kc-arrow)" />
            <text x={(X(o.at) + X(o.arrive)) / 2} y={Y(0) - 12 - i * 10} textAnchor="middle" style={{ ...Tm, fontSize: 9.5 }}>lead time {p.lead}d · +{p.order}</text></>}
        </g>
      ))}
      <defs><marker id="kc-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill={col("muted")} /></marker></defs>
      <line x1={L} x2={W - R} y1={Y(0)} y2={Y(0)} stroke={col("border-strong")} />
      {[0, 30, 60, 90, 120].filter(d => d <= p.days).map(d => <text key={d} x={X(d)} y={H - 10} textAnchor={d === 0 ? "start" : d === p.days ? "end" : "middle"} style={Tm}>day {d}</text>)}
    </svg>
  );
}

// Ending inventory as units × historical cost, SKU by SKU.
export function Stack({ rows }) {
  const vals = rows.map(r => r.units * r.cost);
  const total = vals.reduce((a, b) => a + b, 0);
  const tones = ["primary", "accent", "info", "warning"];
  return (
    <div className="space-y-3" data-testid="kc-diagram-stack">
      <div className="flex h-8 rounded-sm overflow-hidden border border-border">
        {rows.map((r, i) => <div key={r.label} style={{ width: `${(vals[i] / total) * 100}%`, background: col(tones[i % 4], 0.75) }} title={r.label} />)}
      </div>
      <div className="divide-y divide-border border border-border rounded-sm">
        {rows.map((r, i) => (
          <div key={r.label} className="grid grid-cols-[auto_1fr_auto] gap-3 items-center px-3 py-2 text-[12.5px]">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: col(tones[i % 4], 0.75) }} />
            <span>{r.label} <span className="text-muted font-mono-tabular">· {fmt(r.units)} × {peso(r.cost)}</span></span>
            <span className="font-mono-tabular font-semibold">{peso(vals[i])}</span>
          </div>
        ))}
        <div className="grid grid-cols-[1fr_auto] px-3 py-2 text-[12.5px] bg-surface-2/50">
          <span className="font-semibold">Ending Inventory at historical cost</span>
          <span className="font-mono-tabular font-semibold text-primary">{peso(total)}</span>
        </div>
      </div>
    </div>
  );
}
